import { SensorType } from '../../types/sensor-type';
import {
  Gateway,
  NSVA_DEFAULT_VERSION,
  NSVACloudAzureData,
  NSVACloudPlatform,
  NSVAScanInterface,
  NSVAScanTarget,
  Sensor,
  SensorStatus,
  Subnet
} from '../../../common/types';
import {
  WEB_COMPONENT_GZ_LABELED_CONTENT,
  WEB_COMPONENT_GZ_TOOLTIP
} from '../../../../webcomponents.constants';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { GzLabeledContentWebComponent } from '../../../../ui/gzLabeledContent';
import moment from 'moment';
import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { BaseWebComponentTranslator } from '../../../../framework/BaseWebComponentTranslator';
import { html } from 'lit';
import { ShowMoreItemsWebComponent } from '../../../../edr/incidentDetailsSections/showMoreItems';
import { htmlEncode } from '../../../../utils/escape.utils';

export class NsvaNetworkSensorView extends BaseWebComponentView {
  private static HOSTNAME = 'hostname';
  private static IP = 'ip';
  private static DCHP_ENABLED = 'dhcpEnabled';
  private static COMMUNICATION_INTERFACE = 'communicationInterface';
  private static PROBE_GROUP_ID = 'groupId';
  private static MONITORED_NETWORK_KEY = 'monitoredNetwork';
  private static NETWORK_ADDRESS = 'subnet';
  private static VLAN_ID = 'vlanId';
  public static LAST_REPORTED_DATE = 'lastReportedDate';
  public static MAC = 'mac';
  public static NAME = 'name';
  private static CLOUD_AZURE_LOCATION = 'location';
  private static CLOUD_AZURE_NAME = 'name';
  private static CLOUD_AZURE_RESOURCE_GROUP_NAME = 'resourceGroupName';
  private static CLOUD_AZURE_SUBSCRIPTION_ID = 'subscriptionId';
  private static CLOUD_AZURE_VMID = 'vmId';
  private static CLOUD_AZURE_VM_SIZE = 'vmSize';

  public static sensorStatusesToBullets = {
    [SensorStatus.ACTIVE]: 'bullet-success',
    [SensorStatus.DISABLED]: 'bullet-warning',
    [SensorStatus.NOT_CONFIGURED]: 'bullet-neutral',
    [SensorStatus.ACTION_NEEDED]: 'bullet-danger',
  };

  private static vulnerabilityScannerStatus: SensorStatus;

  static getRequirements(dependencies: any): string {
    return `
        <div class="sensor-requirement" sensor-type="${SensorType.NSVA}">
          <gz-title class="sensor-title">${dependencies.translateFn('Make sure the following requirements are met:')}
          </gz-title>
          <ul>
            <li>${dependencies.translateFn('A virtual machine should be set up with 2 CPU x 2 GHz and 2 GB RAM.')}</li>
            <li>${dependencies.translateFn('Access to either vSphere or Hyper V, to create the virtual machine.')}</li>
          </ul>
          <p>${dependencies.translateFn(
            'For a complete Network sensor integration guide, including steps on how to deploy the sensor using vSphere and Hyper-V Manager, refer to {detailLink}.',
            {
              detailLink:
                '<gz-link type="underline" href="https://www.bitdefender.com/business/support/en/77211-452342-the-network-sensor.html"><span>' +
                dependencies.translateFn('this KB article') +
                '</span></gz-link>',
            }
          )}</p>
          <br />
          <p>${dependencies.translateFn(
            'Note: The sensor will automatically be added to the Sensors Management list after deployment.'
          )}</p>
        </div>
        `;
  }

  static getDetailPanelWarningMessage(dependencies: any): string {
    return `<div class="pt-1 pl-3 pb-1" style="color: var(--body-text-arrows-400);">
              <span class="ds-icon ds-icon-notification_warning warning mr-2" style="float: left; margin-right: 5px; font-size: 20px"></span>
              <span style="white-space: normal; width: 360px"> ${dependencies.translateFn(
                'To delete the integration, the Network Sensor Virtual Appliance (NSVA) must be offline.'
              )}</span>
            </div>
        `;
  }

  static buildSensorSubnet(subnet: Subnet, index: number, dependencies: any): GzLabeledContentWebComponent {
    const element = new GzLabeledContentWebComponent();
    element.label = dependencies.translateFn('Network') + ' ' + (index + 1);
    element.stacked = true;
    element.className = 'gz-labeled-content-indent';
    element.innerHTML = ``;

    if (subnet.subnet) {
      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.NETWORK_ADDRESS,
        subnet.subnet,
        dependencies
      );
    }

    if (subnet.isTrafficTagged !== null) {
      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.VLAN_ID,
        subnet.vlanId ? subnet.vlanId.toString() : dependencies.translateFn('Untagged'),
        dependencies
      );
    }

    return element;
  }

  static buildSensorCloud(platform: string, data: NSVACloudAzureData, dependencies: any): GzLabeledContentWebComponent {
    const element = new GzLabeledContentWebComponent();
    element.className = 'integration-sensor';
    element.label = this.getNSVACloudTitleLabel(platform, dependencies);
    element.labelFontWeight = FontWeights.MEDIUM;
    element.stacked = true;
    element.innerHTML = ``;

    if (platform === NSVACloudPlatform.AZURE) {
      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_SUBSCRIPTION_ID,
        data.subscriptionId ?? dependencies.translateFn('N/A'),
        dependencies
      );

      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_RESOURCE_GROUP_NAME,
        data.resourceGroupName ?? dependencies.translateFn('N/A'),
        dependencies
      );

      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_LOCATION,
        data.location ?? dependencies.translateFn('N/A'),
        dependencies
      );

      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_NAME,
        data.name ?? dependencies.translateFn('N/A'),
        dependencies
      );

      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_VMID,
        data.vmId ?? dependencies.translateFn('N/A'),
        dependencies
      );

      element.innerHTML += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.CLOUD_AZURE_VM_SIZE,
        data.vmSize ?? dependencies.translateFn('N/A'),
        dependencies
      );
    }

    return element;
  }

  static getNSVACloudTitleLabel(platform: string, dependencies: any): string {
    switch (platform) {
      case NSVACloudPlatform.AZURE:
        return dependencies.translateFn('Azure VM');
      default:
        return '';
    }
  }

  static buildSensorGateways(gateways: Gateway[], dependencies: any) {
    const items = gateways.map(
      gateway => html`<div class="integration-sensor-gateway-element pb-1">${gateway.ip}; ${gateway.mac}</div>`
    );

    const element = new ShowMoreItemsWebComponent();
    element.items = items;
    (element.className = 'show-more-component'), (element.showMoreButtonText = dependencies.translateFn('Show more'));
    element.title = dependencies.translateFn('Routers');
    element.stacked = true;
    return element;
  }

  static buildEmptyStateSection(sectionTitle: string, sectionMessage: string): GzLabeledContentWebComponent {
    const element = new GzLabeledContentWebComponent();
    element.label = sectionTitle;
    element.innerHTML = `<div class="integration-sensor-error">${sectionMessage}</div>`;

    return element;
  }

  static buildEmptyStateVirtualApplianceSection(sectionTitle: string, sectionMessage: string): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    element.label = sectionTitle;
    element.innerHTML = `<div style="font-style: italic; color: var(--red-alert-500-severe); white-space: normal">
      ${sectionMessage}
    </div>`;
    return element;
  }

  static getSensorSpecifics(sensor: Sensor, dependencies: any): string {
    let result = ``;
    if (sensor.specifics.groupId) {
      result += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.PROBE_GROUP_ID,
        sensor.specifics.groupId.toString(),
        dependencies
      );
    }
    if (sensor.specifics.monitoredNetwork) {
      result += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.MONITORED_NETWORK_KEY,
        sensor.specifics.monitoredNetwork,
        dependencies
      );
    }
    return result;
  }

  static buildCommonData(sensor: Sensor, dependencies: any): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    element.className = 'integration-sensor';
    element.label = dependencies.translateFn('NSVA configuration');
    element.labelFontWeight = FontWeights.MEDIUM;
    element.stacked = true;

    element.appendChild(this.buildSensorTypeHtml(sensor, dependencies));
    if (sensor.specifics.mgmtInterface?.hostname) {
      element.appendChild(
        NsvaNetworkSensorView.buildSensorCommonInfo(
          NsvaNetworkSensorView.HOSTNAME,
          sensor.specifics.mgmtInterface?.hostname,
          dependencies
        )
      );
    }
    if (sensor.specifics.mgmtInterface?.ip) {
      element.appendChild(
        NsvaNetworkSensorView.buildSensorCommonInfo(
          NsvaNetworkSensorView.IP,
          sensor.specifics.mgmtInterface?.ip,
          dependencies
        )
      );
    }
    if (sensor.specifics.mgmtInterface?.dhcpEnabled) {
      element.appendChild(
        NsvaNetworkSensorView.buildSensorCommonInfo(
          NsvaNetworkSensorView.DCHP_ENABLED,
          sensor.specifics.mgmtInterface?.dhcpEnabled
            ? dependencies.translateFn('Yes')
            : dependencies.translateFn('No'),
          dependencies
        )
      );
    }
    if (sensor.specifics.mgmtInterface?.communicationInterface) {
      element.appendChild(
        NsvaNetworkSensorView.buildSensorCommonInfo(
          NsvaNetworkSensorView.COMMUNICATION_INTERFACE,
          sensor.specifics.mgmtInterface?.communicationInterface,
          dependencies
        )
      );
    }
    if (sensor.specifics.lastReportedDate) {
      element.appendChild(
        NsvaNetworkSensorView.buildSensorCommonInfo(
          NsvaNetworkSensorView.LAST_REPORTED_DATE,
          moment(sensor.specifics.lastReportedDate).format('DD MMM YYYY, HH:mm'),
          dependencies
        )
      );
    }

    return element;
  }

  static buildSensorTypeHtml(sensor: Sensor, dependencies: any): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    element.label = dependencies.translateFn('Sensor type');
    element.innerHTML = `<${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${
      FontSizes.PARAGRAPH_12
    }"
    tooltipText="${dependencies.translateFn('Network Sensor')}"
    >
        ${dependencies.translateFn('Network Sensor')}
        </${WEB_COMPONENT_GZ_TOOLTIP}>`;
    element.className = 'gz-labeled-content-indent';
    return element;
  }

  static buildSensorDetailInfo(key: string, value: string, dependencies: any): string {
    let label = '';
    switch (key) {
      case NsvaNetworkSensorView.MONITORED_NETWORK_KEY:
        label = dependencies.translateFn('vSwitch port group');
        break;
      case NsvaNetworkSensorView.HOSTNAME:
        label = dependencies.translateFn('Hostname');
        break;
      case NsvaNetworkSensorView.IP:
        label = dependencies.translateFn('IP');
        break;
      case NsvaNetworkSensorView.DCHP_ENABLED:
        label = dependencies.translateFn('Configured via DHCP');
        break;
      case NsvaNetworkSensorView.COMMUNICATION_INTERFACE:
        label = dependencies.translateFn('Communication interface');
        break;
      case NsvaNetworkSensorView.PROBE_GROUP_ID:
        label = dependencies.translateFn('Probe Group ID');
        break;
      case NsvaNetworkSensorView.NETWORK_ADDRESS:
        label = dependencies.translateFn('Network address');
        break;
      case NsvaNetworkSensorView.VLAN_ID:
        label = dependencies.translateFn('VLAN ID');
        break;
      case NsvaNetworkSensorView.LAST_REPORTED_DATE:
        label = dependencies.translateFn('Last reported');
        break;
      case NsvaNetworkSensorView.MAC:
        label = dependencies.translateFn('MAC');
        break;
      case NsvaNetworkSensorView.NAME:
        label = dependencies.translateFn('Name');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_LOCATION:
        label = dependencies.translateFn('Location');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_NAME:
        label = dependencies.translateFn('Name');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_RESOURCE_GROUP_NAME:
        label = dependencies.translateFn('Resource group');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_SUBSCRIPTION_ID:
        label = dependencies.translateFn('Subscription ID');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_VMID:
        label = dependencies.translateFn('VM ID');
        break;
      case NsvaNetworkSensorView.CLOUD_AZURE_VM_SIZE:
        label = dependencies.translateFn('VM size');
        break;
    }

    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${label}">
      <${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${FontSizes.PARAGRAPH_12}" tooltipText="${htmlEncode(value)}">
        ${htmlEncode(value)}
      </${WEB_COMPONENT_GZ_TOOLTIP}>
    </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  static buildSensorCommonInfo(key: string, value: string, dependencies: any): HTMLElement {
    let label = '';
    switch (key) {
      case NsvaNetworkSensorView.HOSTNAME:
        label = dependencies.translateFn('Hostname');
        break;
      case NsvaNetworkSensorView.IP:
        label = dependencies.translateFn('IP');
        break;
      case NsvaNetworkSensorView.DCHP_ENABLED:
        label = dependencies.translateFn('Configured via DHCP');
        break;
      case NsvaNetworkSensorView.COMMUNICATION_INTERFACE:
        label = dependencies.translateFn('Communication interface');
        break;
      case NsvaNetworkSensorView.LAST_REPORTED_DATE:
        label = dependencies.translateFn('Last reported');
        break;
    }

    const element = new GzLabeledContentWebComponent();
    element.label = label;
    element.innerHTML = `<${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${
      FontSizes.PARAGRAPH_12
    }"
    tooltipText="${htmlEncode(value)}"
    >
        ${htmlEncode(value)}
        </${WEB_COMPONENT_GZ_TOOLTIP}>`;
    element.className = 'gz-labeled-content-indent';
    return element;
  }

  static buildSensorExtraInfo(sensor: Sensor, dependencies: any): HTMLElement {
    const result = document.createElement('div');
    if (!sensor.specifics.subnets || sensor.specifics.subnets.length === 0) {
      result.appendChild(
        NsvaNetworkSensorView.buildEmptyStateSection(
          dependencies.translateFn('Network 1'),
          dependencies.translateFn('No networks defined!')
        )
      );
    }

    if (sensor.specifics.subnets) {
      for (const [index, subnet] of sensor.specifics.subnets.entries()) {
        result.appendChild(NsvaNetworkSensorView.buildSensorSubnet(subnet, index, dependencies));
      }
    }

    const isNotDefaultVersion = sensor.specifics.version && sensor.specifics.version > NSVA_DEFAULT_VERSION;

    if (isNotDefaultVersion && (!sensor.specifics.gateways || sensor.specifics.gateways.length === 0)) {
      result.appendChild(
        NsvaNetworkSensorView.buildEmptyStateSection(
          dependencies.translateFn('Routers'),
          dependencies.translateFn('No routers defined!')
        )
      );
    }

    if (sensor.specifics.gateways && sensor.specifics.gateways.length !== 0) {
      result.appendChild(NsvaNetworkSensorView.buildSensorGateways(sensor.specifics.gateways, dependencies));
    }

    return result;
  }

  static buildVirtualApplianceInfo(sensor: Sensor, sensorsExtraInfoContainer: HTMLElement, dependencies: any) {
    const element = new GzLabeledContentWebComponent();
    element.className = 'integration-sensor';
    element.label = dependencies.translateFn('Network Vulnerability Scanner module');
    element.labelFontWeight = FontWeights.MEDIUM;
    element.stacked = true;

    sensorsExtraInfoContainer.appendChild(element);
    element.appendChild(this.buildEnabledInPolicy(sensor, dependencies));
    element.appendChild(this.buildVirtualApplianceStatus(sensor));
    if (this.vulnerabilityScannerStatus !== SensorStatus.ACTIVE) {
      element.appendChild(this.buildVirtualApplianceReason(sensor));
    }

    const vulnerabilityScanner = sensor?.specifics?.vulnerabilityScanner;

    if (!vulnerabilityScanner?.scanInterfaces || vulnerabilityScanner.scanInterfaces.length === 0) {
      element.appendChild(
        NsvaNetworkSensorView.buildEmptyStateVirtualApplianceSection(
          dependencies.translateFn('Scan interface 1'),
          dependencies.translateFn('No scan interfaces defined.')
        )
      );
    }

    if (vulnerabilityScanner?.scanInterfaces) {
      for (const [index, scanInterface] of vulnerabilityScanner.scanInterfaces.entries()) {
        element.appendChild(NsvaNetworkSensorView.buildSensorScanInterface(scanInterface, index, dependencies));
      }
    }

    if (!vulnerabilityScanner?.scanTargets || vulnerabilityScanner.scanTargets.length === 0) {
      element.appendChild(
        NsvaNetworkSensorView.buildEmptyStateVirtualApplianceSection(
          dependencies.translateFn('Scan targets'),
          dependencies.translateFn('No scan targets defined.')
        )
      );
    }

    if (vulnerabilityScanner?.scanTargets && vulnerabilityScanner.scanTargets.length !== 0) {
      element.appendChild(NsvaNetworkSensorView.buildSensorScanTarget(vulnerabilityScanner.scanTargets, dependencies));
    }
  }

  private static buildSensorScanInterface(scanInterface: NSVAScanInterface, index: number, dependencies: any) {
    const element = new GzLabeledContentWebComponent();
    element.className = 'gz-labeled-content-indent';
    element.label = dependencies.translateFn('Scan interface {scanInterfaceNumber}', {
      scanInterfaceNumber: index + 1,
    });
    element.stacked = true;
    let elementData = ``;

    if (scanInterface.ip) {
      elementData += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.IP,
        scanInterface.ip,
        dependencies
      );
    }

    if (scanInterface.mac) {
      elementData += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.MAC,
        scanInterface.mac,
        dependencies
      );
    }

    if (scanInterface.name) {
      elementData += NsvaNetworkSensorView.buildSensorDetailInfo(
        NsvaNetworkSensorView.NAME,
        scanInterface.name,
        dependencies
      );
    }

    element.innerHTML = elementData;

    return element;
  }

  static buildVirtualApplianceStatus(sensor: Sensor): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    const status = sensor.vulnerabilityScannerStatus ?? SensorStatus.NOT_CONFIGURED;

    this.vulnerabilityScannerStatus = status;

    const bullet = this.sensorStatusesToBullets[status];
    const translations = BaseWebComponentTranslator.getInstance().getSensorsIntegrationsStrings();
    const label = translations['sensorStatusLabels'][status];
    element.label = translations['statusLabel'];
    element.innerHTML = `<span class="has-bullet ${bullet}">${label}</span>`;

    return element;
  }

  static buildEnabledInPolicy(sensor: Sensor, dependencies): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    const enabledInPolicy = sensor?.specifics?.vulnerabilityScanner?.enabled;
    const value = enabledInPolicy ? dependencies.translateFn('Yes') : dependencies.translateFn('No');

    const translations = BaseWebComponentTranslator.getInstance().getSensorsIntegrationsStrings();
    element.label = translations['enabledInPolicyLabel'];
    element.innerHTML = `<${WEB_COMPONENT_GZ_TOOLTIP} class="integration-sensor-network" fontSize="${
      FontSizes.PARAGRAPH_12
    }"
    tooltipText="${value}"
    >
        ${value}
        </${WEB_COMPONENT_GZ_TOOLTIP}>`;

    return element;
  }

  static buildVirtualApplianceReason(sensor: Sensor): HTMLElement {
    const element = new GzLabeledContentWebComponent();
    const translations = BaseWebComponentTranslator.getInstance().getSensorsIntegrationsStrings();
    const reason = sensor?.specifics?.vulnerabilityScanner?.reason
      ? translations['errorTextsVulnerabilityAppliance'][
        sensor.specifics.vulnerabilityScanner.reason
        ]
      : translations['unknownName'];
    element.label = translations['sensorErrorLabel'];
    element.innerHTML = `<${WEB_COMPONENT_GZ_TOOLTIP} style="font-style: italic; color: var(--red-alert-500-severe); white-space: normal" fontSize="${
      FontSizes.PARAGRAPH_12
    }"
    tooltipText="${reason}"
    >
      ${reason}
    </${WEB_COMPONENT_GZ_TOOLTIP}>`;
    return element;
  }

  private static buildSensorScanTarget(scanTargets: NSVAScanTarget[], dependencies): HTMLElement {
    const items = scanTargets.map(
      scanTarget => html`<div class="integration-sensor-subnet-element pb-1">${scanTarget.subnet}</div>`
    );

    const element = new ShowMoreItemsWebComponent();
    element.items = items;
    (element.className = 'show-more-component'), (element.showMoreButtonText = dependencies.translateFn('Show more'));
    element.title = dependencies.translateFn('Scan targets');
    element.stacked = true;
    return element;
  }
}
